import React from "react";
import { Link } from "react-router-dom";
import { useT } from "@/lib/i18n";

const IMPRESSUM_URL = "https://versteckmich.de/FicosauRusLex/impressum";
const DATENSCHUTZ_URL = "https://versteckmich.de/FicosauRusLex/datenschutz";

/**
 * Site-wide footer. Renders the two legal links required in DE jurisdictions
 * (Impressum + Datenschutz) plus a copyright line. Always opens external
 * legal docs in a new tab with rel=noopener,noreferrer.
 *
 * Variants:
 *   variant="full"     — used inside AppShell (gated app routes), wide layout
 *   variant="compact"  — used on auth/landing routes, fits centered narrow pages
 */
export default function Footer({ variant = "full" }) {
  const { t } = useT();
  const compact = variant === "compact";
  const year = new Date().getFullYear();

  return (
    <footer
      data-testid="site-footer"
      className={
        compact
          ? "mt-10 pb-6 px-4 text-center"
          : "mt-16 border-t border-white/5 bg-zinc-950/60"
      }
    >
      <div
        className={
          compact
            ? "flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-[11px] text-zinc-500"
            : "max-w-7xl mx-auto px-4 sm:px-6 py-8 flex flex-col sm:flex-row items-center justify-between gap-4"
        }
      >
        {!compact && (
          <p className="text-xs text-zinc-500 order-2 sm:order-1">
            © {year} FicoPilot · {t("footer.tagline") || "The Global Gaming Assistant"}
          </p>
        )}
        <nav
          aria-label="Legal"
          className={
            compact
              ? "flex flex-wrap items-center justify-center gap-x-5 gap-y-2"
              : "flex flex-wrap items-center gap-x-6 gap-y-2 order-1 sm:order-2"
          }
        >
          <Link
            to="/"
            className="text-xs text-zinc-500 hover:text-white transition"
            data-testid="footer-home"
          >
            FicoPilot
          </Link>
          <a
            href={IMPRESSUM_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-zinc-400 hover:text-neon transition font-medium"
            data-testid="footer-impressum"
          >
            {t("footer.impressum") || "Impressum"}
          </a>
          <a
            href={DATENSCHUTZ_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-zinc-400 hover:text-neon transition font-medium"
            data-testid="footer-datenschutz"
          >
            {t("footer.privacy") || "Datenschutz"}
          </a>
        </nav>
        {compact && (
          <p className="w-full text-[11px] text-zinc-600 mt-2">© {year} FicoPilot</p>
        )}
      </div>
    </footer>
  );
}

export { IMPRESSUM_URL, DATENSCHUTZ_URL };
